import React, { useState } from 'react';
import API from '../../services/api';
import Sheet from '../ui/Sheet';
import Icon from '../ui/Icon';
import MotivationMoment from './MotivationMoment';
import QuestionImage from '../ui/QuestionImage';
import QuestionText from '../ui/QuestionText';
import './MistakeRetryModal.css';

// Mistake Bank doesn't store what the student picked historically — only
// mistake_count/is_resolved — so this is honestly a fresh attempt at the
// same question, graded server-side via POST /mistakes/:questionId/retry
// (which reuses gradingEngine.gradeAnswer, the exact function every other
// submission path uses). Never fabricates a "previous answer."
// Presented as a bottom sheet: focus is trapped, Esc / the backdrop close it, the page behind cannot scroll.
const MistakeRetryModal = ({ mistake, onClose, onResolved }) => {
  const [selected, setSelected] = useState(null);
  const [outcome, setOutcome] = useState(null); // { correct, correct_answer, explanation, resolved }
  const [submitting, setSubmitting] = useState(false);
  const [problem, setProblem] = useState('');

  const submit = async () => {
    if (selected == null || submitting) return;
    setSubmitting(true); setProblem('');
    try {
      const res = await API.post(`/mistakes/${mistake.question_id}/retry`, { selected_option: selected });
      setOutcome(res.data);
      if (res.data.resolved) onResolved(mistake.id);
    } catch (err) {
      setProblem(err.response ? 'Could not check your answer. Please try again.' : 'You appear to be offline. Try again when you\'re connected.');
    } finally {
      setSubmitting(false);
    }
  };

  const footer = !outcome ? (
    <button type="button" className="z-btn z-btn--primary mrm-submit" disabled={selected == null || submitting} onClick={submit}>
      {submitting ? 'Checking…' : 'Try Again'}
    </button>
  ) : (
    <button type="button" className="z-btn z-btn--primary mrm-submit" onClick={onClose}>Done</button>
  );

  return (
    <Sheet open onClose={onClose} label="Retry mistake" size="lg" footer={footer}>
      <div className="mrm-card">
        <span className="mrm-topic">{mistake.topic_name}</span>
        <div className="mrm-question"><QuestionText text={mistake.question_text} /></div>
        <QuestionImage path={mistake.image_path} />

        {!outcome ? (
          <div className="mrm-options" role="radiogroup" aria-label="Answer options">
            {(mistake.options || []).map((opt, i) => {
              const optNum = i + 1;
              const on = selected === optNum;
              return (
                <button
                  type="button"
                  key={i}
                  role="radio"
                  aria-checked={on}
                  className={`mrm-opt ${on ? 'mrm-opt--selected' : ''}`}
                  onClick={() => setSelected(optNum)}
                >
                  <span className="mrm-opt-letter">{String.fromCharCode(65 + i)}</span>
                  <span className="mrm-opt-text">{typeof opt === 'object' ? (opt.option_text ?? opt.text ?? '') : opt}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="mrm-outcome">
            <div className={`mrm-outcome-badge ${outcome.correct ? 'mrm-outcome-badge--correct' : 'mrm-outcome-badge--wrong'}`}>
              <Icon name={outcome.correct ? 'success' : 'error'} size={18} /> {outcome.correct ? 'Recovered' : 'Not quite'}
            </div>
            {!outcome.correct && (
              <p className="mrm-correct-answer">Correct answer: <strong>{outcome.correct_answer}</strong></p>
            )}
            {outcome.explanation && <p className="mrm-explanation">{outcome.explanation}</p>}
            <MotivationMoment context={outcome.correct ? 'recovery' : 'mistake_review'} seed={mistake.question_id} />
          </div>
        )}
        {problem && <p className="z-error-text" role="alert">{problem}</p>}
      </div>
    </Sheet>
  );
};

export default MistakeRetryModal;
